import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styles from './ComplaintDetailView.module.css';
import { fetchComplaintById, ComplaintDetail, fetchQualityControl, QualityControlItem, updateComplaintReposicao, searchOrders, OrderSearchResult } from '../services/complaintsAPI';
import ComplaintDetailSkeleton from './ComplaintDetailSkeleton';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoIcon from '@mui/icons-material/Info';
import ImageOverlay from './Shared/ImageViewer/ImageOverlay';
import { formatProtocol } from '../ComplaintsPage';
import { formatDateSafely } from '../utils/formatters';


// Formatters for Brazilian Currency
const formatFloatToBRL = (val: number | string | null) => {
    if (val == null || val === '') return '';
    const floatVal = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(floatVal)) return '';
    return floatVal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatInputToBRL = (val: string) => {
    const onlyDigits = val.replace(/\D/g, '');
    if (!onlyDigits) return '';
    const num = (parseInt(onlyDigits, 10) / 100).toFixed(2);
    return num.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

interface ComplaintDetailViewProps {
    complaintId: number;
    onViewOrder?: (orderId: number) => void;
    autoFocusReposicao?: boolean;
}

const ComplaintDetailView: React.FC<ComplaintDetailViewProps> = ({ complaintId, onViewOrder, autoFocusReposicao }) => {
    const queryClient = useQueryClient();

    // React Query for fetches
    const { data: complaint, isLoading: complaintLoading, error: queryError } = useQuery({
        queryKey: ['complaint', complaintId],
        queryFn: () => fetchComplaintById(complaintId),
        enabled: !!complaintId,
        staleTime: 5 * 60 * 1000 // Cache for 5 minutes
    });

    const { data: qualityItemsQuery, isLoading: qualityLoading } = useQuery({
        queryKey: ['qualityControl', complaintId],
        queryFn: () => fetchQualityControl(complaintId),
        enabled: !!complaintId,
        staleTime: 5 * 60 * 1000
    });

    const qualityItems = qualityItemsQuery || [];
    const needsReposicaoLink = !!complaint && qualityItems.some(qc => qc.autorizado === 'Autorizado') && (!complaint.valorReposicao || Number(complaint.valorReposicao) === 0);
    const loading = complaintLoading;
    const error = queryError ? (queryError as any)?.response?.data?.error || 'Erro ao carregar detalhes.' : null;

    // Image Viewer State
    const [previewIndex, setPreviewIndex] = useState<{ index: number; type: 'foto' | 'etiqueta' } | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<'ocorrencia' | 'analise'>(() => {
        const tabFromUrl = new URLSearchParams(window.location.search).get('tab');
        return (tabFromUrl === 'ocorrencia' || tabFromUrl === 'analise') ? tabFromUrl : 'ocorrencia';
    });

    // Reposição State (Complaint Level)
    const [reposicaoEdits, setReposicaoEdits] = useState<{ valor: string; info: string }>({ valor: '', info: '' });
    const [savingReposicao, setSavingReposicao] = useState(false);
    const [isEditingReposicao, setIsEditingReposicao] = useState(false);

    const orderSearchInputRef = useRef<HTMLInputElement>(null);

    const handleStartEditReposicao = () => {
        setIsEditingReposicao(true);
        setTimeout(() => {
            if (orderSearchInputRef.current) {
                orderSearchInputRef.current.focus();
                orderSearchInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 80);
    };

    // Order autocomplete state for reposition
    const [orderSearch, setOrderSearch] = useState<string>('');
    const [orderResults, setOrderResults] = useState<OrderSearchResult[]>([]);
    const [selectedOrders, setSelectedOrders] = useState<OrderSearchResult[]>([]);
    const [orderSearchLoading, setOrderSearchLoading] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastInitializedIdRef = useRef<number | null>(null);

    // Toast state
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({ show: false, message: '', type: 'success' });

    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3500);
            return () => clearTimeout(timer);
        }
    }, [toast.show]);

    // Initialize State derived from complaint data
    useEffect(() => {
        if (!complaint) return;

        // Initialize reposition edits from complaint data
        setReposicaoEdits({
            valor: formatFloatToBRL(complaint.valorReposicao),
            info: complaint.infoReposicao || ''
        });

        if (complaint.valorReposicao && Number(complaint.valorReposicao) > 0) {
            setIsEditingReposicao(false);
        } else {
            setIsEditingReposicao(true);
        }

        // Discovery Signaling: Auto-focus Analysis Tab if data exists (only when loading a different complaint)
        if (lastInitializedIdRef.current !== complaint.id) {
            const tabFromUrl = searchParams.get('tab');
            if (tabFromUrl === 'ocorrencia' || tabFromUrl === 'analise') {
                setActiveTab(tabFromUrl);
            } else if (complaint.qcInfoReposicao || complaint.parecerSac || (complaint.valorReposicao && complaint.valorReposicao > 0) || autoFocusReposicao) {
                setActiveTab('analise');
            } else {
                setActiveTab('ocorrencia');
            }
            lastInitializedIdRef.current = complaint.id;
        }

        if (autoFocusReposicao) {
            setTimeout(() => {
                if (orderSearchInputRef.current) {
                    orderSearchInputRef.current.focus();
                    orderSearchInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 300);
        }

        const loadOrderDetails = async () => {
            if (complaint.infoReposicao) {
                const entries = complaint.infoReposicao.split(',').map(c => c.trim()).filter(Boolean);
                const loaded = await Promise.all(
                    entries.map(async entry => {
                        const match = entry.match(/^#?(\d+)/);
                        const code = match ? match[1] : entry.replace(/^#/, '').trim();
                        try {
                            const results = await searchOrders(code);
                            const found = results.find(o => String(o.code) === String(code) || String(o.id) === String(code));
                            if (found) {
                                return found;
                            }
                        } catch (err) {
                            console.error('Erro ao carregar detalhes do pedido vinculado:', err);
                        }
                        const name = entry.includes('-') ? entry.split('-')[1].trim() : '';
                        return { code, clientName: name, totalValue: 0 } as OrderSearchResult;
                    })
                );
                setSelectedOrders(loaded);
            } else {
                setSelectedOrders([]);
            }
        };
        loadOrderDetails();
    }, [complaint, autoFocusReposicao, searchParams]);

    // Sincroniza aba ativa → URL (uma única direção)
    useEffect(() => {
        if (!complaint) return;
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('tab', activeTab);
            return next;
        }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, complaint?.id]);
    // Order search for reposition autocomplete
    const handleOrderSearch = useCallback((query: string) => {
        setOrderSearch(query);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (query.length < 2) {
            setOrderResults([]);
            return;
        }

        // Debounce 400ms
        searchTimeoutRef.current = setTimeout(async () => {
            setOrderSearchLoading(true);
            try {
                const results = await searchOrders(query);
                // Filtro rigoroso: Ignora itens sem código ou com #N/A literal
                const filtered = results.filter((order: OrderSearchResult) => {
                    const c = (order.code || '').toString().trim().toUpperCase();
                    return c !== '' && c !== 'N/A' && c !== '#N/A' && c !== '#NA' && c !== 'NA';
                });
                setOrderResults(filtered);
            } catch (err) {
                console.error('Erro ao buscar pedidos:', err);
            } finally {
                setOrderSearchLoading(false);
            }
        }, 400);
    }, []);

    const getStatusClass = (situacao: string) => {
        switch (situacao) {
            case 'ABERTA': return styles.statusSoftAberta;
            case 'EM_ANALISE': return styles.statusSoftEmAnalise;
            case 'CONCLUIDA': return styles.statusSoftConcluida;
            case 'PENDENTE': return styles.statusSoftPendente;
            case 'REJEITADA': return styles.statusSoftRejeitada;
            default: return '';
        }
    };

    const getStatusLabel = (situacao: string) => {
        switch (situacao) {
            case 'ABERTA': return 'Aberta';
            case 'EM_ANALISE': return 'Em Análise';
            case 'CONCLUIDA': return 'Concluída';
            case 'PENDENTE': return 'Pendente';
            case 'REJEITADA': return 'Rejeitada';
            default: return situacao;
        }
    };

    const getRefundLabel = (type: string) => {
        switch (type) {
            case 'CREDITO': return 'Gerar crédito';
            case 'DESCONTO': return 'Desconto no boleto';
            case 'REPOSICAO': return 'Reposição';
            case 'NENHUM': return 'Nenhum';
            default: return type || '-';
        }
    };

    const formatDate = (dateStr?: string) => {
        return formatDateSafely(dateStr);
    };

    const getAutorizadoClass = (autorizado: string) => {
        if (autorizado === 'Autorizado') return styles.qcAutorizado;
        if (autorizado === 'Pendente') return styles.qcPendente;
        return styles.qcOutro;
    };

    const handleSelectOrder = (order: OrderSearchResult) => {
        // Prevent duplicates
        if (selectedOrders.some(o => (o.code || String(o.id)) === (order.code || String(order.id)))) {
            setOrderSearch('');
            setOrderResults([]);
            return;
        }

        const updatedOrders = [...selectedOrders, order];
        setSelectedOrders(updatedOrders);

        setReposicaoEdits(prev => {
            const currentRaw = prev.valor ? parseFloat(prev.valor.replace(/\./g, '').replace(',', '.')) : 0;
            const orderValue = Number(order.totalValue || 0);
            const newTotal = currentRaw + orderValue;

            return {
                ...prev,
                info: updatedOrders.map(o => o.clientName ? `#${o.code || o.id} - ${o.clientName}` : `#${o.code || o.id}`).join(', '),
                valor: formatFloatToBRL(newTotal)
            };
        });
        setOrderSearch('');
        setOrderResults([]);
    };

    const handleRemoveOrder = (indexToRemove: number) => {
        const orderToRemove = selectedOrders[indexToRemove];
        const updatedOrders = selectedOrders.filter((_, i) => i !== indexToRemove);
        setSelectedOrders(updatedOrders);

        setReposicaoEdits(prev => {
            const currentRaw = prev.valor ? parseFloat(prev.valor.replace(/\./g, '').replace(',', '.')) : 0;
            const orderValue = Number(orderToRemove.totalValue || 0);
            const newTotal = updatedOrders.length === 0 ? 0 : Math.max(0, currentRaw - orderValue);

            return {
                ...prev,
                info: updatedOrders.map(o => o.clientName ? `#${o.code || o.id} - ${o.clientName}` : `#${o.code || o.id}`).join(', '),
                valor: formatFloatToBRL(newTotal)
            };
        });
    };

    const handleSaveReposicao = async () => {
        if (!complaintId) return;

        setSavingReposicao(true);
        try {
            const rawValor = reposicaoEdits.valor
                ? parseFloat(reposicaoEdits.valor.replace(/\./g, '').replace(',', '.'))
                : undefined;

            await updateComplaintReposicao(complaintId, {
                valorReposicao: rawValor,
                // Passa null explicitamente quando vazio para forçar a limpeza no banco
                infoReposicao: reposicaoEdits.info !== '' ? reposicaoEdits.info : null
            });
            // Refetch to ensure React Query is synchronized
            queryClient.invalidateQueries({ queryKey: ['complaint', complaintId] });
            setIsEditingReposicao(false);
            setToast({ show: true, message: 'Reposição salva com sucesso!', type: 'success' });
        } catch (err: any) {
            console.error('Erro ao salvar reposição:', err);
            setToast({ show: true, message: err?.response?.data?.error || 'Erro ao salvar informações de reposição.', type: 'error' });
        } finally {
            setSavingReposicao(false);
        }
    };

    if (loading) {
        return <ComplaintDetailSkeleton styles={styles} />;
    }

    if (error || !complaint) {
        return (
            <div className={styles.errorContainer}>
                <span style={{ color: '#dc2626' }}>{error || 'Reclamação não encontrada.'}</span>
            </div>
        );
    }

    return (
        <div className={styles.viewContainer}>
            {/* Header Redesign as Clean Document Style */}
            <div className={styles.pageHeaderSection}>
                <div className={styles.headerMainArea}>
                    <div className={styles.headerTitleRow}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span className={styles.metaLabel} style={{ marginBottom: 0 }}>Cliente</span>
                            <h2 className={styles.mainTitle}>{complaint.clienteNome}</h2>
                        </div>
                        <span className={`${styles.statusPill} ${getStatusClass(complaint.situacao)}`}>
                            {getStatusLabel(complaint.situacao)}
                        </span>
                    </div>

                    <div className={styles.headerMetaGrid}>
                        <div className={styles.metaBlock}>
                            <span className={styles.metaLabel}>Protocolo</span>
                            <span className={styles.metaValue}>#{formatProtocol(complaint.protocoloSac) || complaint.id}</span>
                        </div>
                        <div className={styles.metaBlock}>
                            <span className={styles.metaLabel}>Data Abertura</span>
                            <span className={styles.metaValue}>{formatDate(complaint.dataAbertura)}</span>
                        </div>
                        {complaint.clienteDocumento && (
                            <div className={styles.metaBlock}>
                                <span className={styles.metaLabel}>Documento</span>
                                <span className={styles.metaValue}>{complaint.clienteDocumento}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tab Navigation Dashboard Style */}
            <div className={styles.tabBar}>
                <button
                    className={`${styles.tabButton} ${activeTab === 'ocorrencia' ? styles.tabButtonActive : ''}`}
                    onClick={() => setActiveTab('ocorrencia')}
                >
                    <ListAltIcon fontSize="small" />
                    Reclamação
                </button>
                <button
                    className={`${styles.tabButton} ${activeTab === 'analise' ? styles.tabButtonActive : ''}`}
                    onClick={() => setActiveTab('analise')}
                >
                    <AssignmentIcon fontSize="small" />
                    Análise Técnica
                    {needsReposicaoLink && (
                        <span className={styles.notificationDot}></span>
                    )}
                </button>
            </div>

            {/* Content Body with Tab Logic */}
            <div className={styles.contentSection}>
                {activeTab === 'analise' ? (
                    <div className={styles.tabContentArea}>
                        {/* Primary Hero Section: Technical Report (Diagnostic) */}
                        {/* Parecer Técnico (Diagnostic) */}
                        {(complaint.qcInfoReposicao || complaint.parecerSac) && qualityItems.length === 0 && (
                            <div className={styles.infoCard}>
                                <div className={styles.cardHeader}>
                                    <AssignmentIcon fontSize="small" className={styles.cardIcon} style={{ color: '#64748b' }} />
                                    <span className={styles.cardTitle}>Parecer Técnico</span>
                                </div>
                                <div className={styles.technicalOpinionText} style={{ marginTop: '12px' }}>
                                    {complaint.qcInfoReposicao || complaint.parecerSac}
                                </div>
                            </div>
                        )}

                        {/* Controle de Qualidade (Defects per item) */}
                        {qualityItems.length > 0 && (
                            <div className={styles.infoCard}>
                                <div className={styles.cardHeader}>
                                    <span className={styles.cardTitle}>Análise da Qualidade</span>
                                    <span className={styles.itemCountBadge} style={{ marginLeft: 'auto' }}>{qualityItems.length}</span>
                                </div>

                                <div className={styles.qcGrid} style={{ marginTop: '16px' }}>
                                    {qualityItems.map(qcItem => (
                                        <div key={qcItem.id} className={styles.qcCard}>
                                            <div className={styles.qcCardHeader}>
                                                <div className={styles.qcItemInfo}>
                                                    <span className={styles.referenceBadge}>{qcItem.referencia || qcItem.itemReferencia || '-'}</span>
                                                    <span className={styles.qcItemDesc}>{qcItem.itemDescricao || qcItem.descricaoDefeito || '-'}</span>
                                                </div>
                                                <span className={`${styles.qcStatusBadge} ${getAutorizadoClass(qcItem.autorizado)}`}>
                                                    {qcItem.autorizado === 'Autorizado' ? 'Reposição' : qcItem.autorizado}
                                                </span>
                                            </div>

                                            <div className={styles.qcDetailsGrid}>
                                                {qcItem.gravidade && (
                                                    <div className={styles.qcDetail}>
                                                        <label>Gravidade</label>
                                                        <span data-gravidade={qcItem.gravidade}>{qcItem.gravidade}</span>
                                                    </div>
                                                )}
                                                {qcItem.causa && (
                                                    <div className={styles.qcDetail}>
                                                        <label>Causa</label>
                                                        <span>{qcItem.causa}</span>
                                                    </div>
                                                )}
                                                {qcItem.procedencia && (
                                                    <div className={styles.qcDetail}>
                                                        <label>Procedência</label>
                                                        <span>{qcItem.procedencia}</span>
                                                    </div>
                                                )}
                                                {qcItem.modoFalha && (
                                                    <div className={styles.qcDetail}>
                                                        <label>Modo de Falha</label>
                                                        <span>{qcItem.modoFalha}</span>
                                                    </div>
                                                )}
                                                {qcItem.naoConformidade && (
                                                    <div className={styles.qcDetail} style={{ gridColumn: '1 / -1' }}>
                                                        <label>Não Conformidade</label>
                                                        <span>{qcItem.naoConformidade}</span>
                                                    </div>
                                                )}
                                                {qcItem.infoReposicao && (
                                                    <div className={styles.qcDetail} style={{ gridColumn: '1 / -1' }}>
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <AssignmentIcon fontSize="inherit" style={{ color: '#64748b', fontSize: '14px' }} />
                                                            Parecer Técnico
                                                        </label>
                                                        <span>{qcItem.infoReposicao}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Resolução de Reposição */}
                        {qualityItems.some(qc => qc.autorizado === 'Autorizado') && (
                            <div className={styles.infoCard}>
                                <div className={styles.cardHeader}>
                                     <LocalShippingIcon fontSize="small" className={styles.cardIcon} style={{ color: '#15803d' }} />
                                     <span className={styles.cardTitle}>Vincular Pedido de Reposição</span>
                                </div>

                                <div className={styles.reposicaoSectionUnified} style={{ border: 'none', background: 'transparent', padding: 0 }}>
                                    {isEditingReposicao ? (
                                        <div className={styles.reposicaoEditForm}>
                                            <div className={styles.reposicaoFormRow}>
                                                <div className={styles.reposicaoFormGroup} style={{ flex: 2 }}>
                                                    <label className={styles.formLabel}>1. Vincular Pedido de Reposição</label>
                                                    <p className={styles.stepHint} style={{ margin: '0 0 8px 0' }}>
                                                        Busque e selecione o pedido. O valor será preenchido automaticamente.
                                                    </p>
                                                    <div className={styles.orderSearchWrapper}>
                                                        <div className={styles.searchInputContainer}>
                                                            <SearchIcon className={styles.searchIconMui} />
                                                            <input
                                                                ref={orderSearchInputRef}
                                                                type="text"
                                                                className={`${styles.reposicaoInput} ${styles.reposicaoInputWithIcon}`}
                                                                placeholder="Digite código, nome ou ID..."
                                                                value={orderSearch || ''}
                                                                onChange={e => handleOrderSearch(e.target.value)}
                                                            />
                                                        </div>
                                                        {orderSearchLoading && (
                                                            <div className={styles.searchSkeletonContainer}>
                                                                {[1, 2, 3].map(i => (
                                                                    <div key={i} className={styles.searchSkeletonRow}>
                                                                        <div className={styles.searchSkeletonCode} />
                                                                        <div className={styles.searchSkeletonInfo}>
                                                                            <div className={styles.searchSkeletonLine} style={{ width: '60%' }} />
                                                                            <div className={styles.searchSkeletonLine} style={{ width: '35%' }} />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {orderResults.length > 0 && (
                                                            <div className={styles.orderDropdown}>
                                                                {orderResults.map(order => (
                                                                    <div
                                                                        key={order.id}
                                                                        className={styles.orderDropdownItem}
                                                                        onClick={() => handleSelectOrder(order)}
                                                                    >
                                                                        <div className={styles.orderDropdownCode}>{order.code}</div>
                                                                        <div className={styles.orderDropdownClient}>
                                                                            <span style={{ fontWeight: 600, color: '#1e293b' }} title={order.clientName}>{order.clientName}</span>
                                                                            <span style={{ fontSize: '0.8rem', color: '#94a3b8', display: 'block' }}>ID: {order.id}</span>
                                                                        </div>
                                                                        {order.emissionDate && (
                                                                            <div className={styles.orderDropdownDate} style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                                                {formatDateSafely(order.emissionDate)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {selectedOrders.length > 0 && (
                                                        <div className={styles.selectedOrdersList}>
                                                            {selectedOrders.map((order, idx) => (
                                                                <div key={idx} className={styles.selectedOrderBadge}>
                                                                    <span className={styles.selectedOrderCode}>{order.code || order.id}</span>
                                                                    {order.clientName && (
                                                                        <span className={styles.selectedOrderClient}>{order.clientName}</span>
                                                                    )}
                                                                    <button
                                                                        className={styles.removeOrderBtn}
                                                                        onClick={() => handleRemoveOrder(idx)}
                                                                        title="Remover vínculo"
                                                                    >
                                                                        &times;
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className={styles.reposicaoFormGroup} style={{ flex: 1 }}>
                                                    <label className={styles.formLabel}>2. Valor da Reposição (R$)</label>
                                                    <p className={styles.stepHint} style={{ margin: '0 0 8px 0' }}>
                                                        Ajuste manualmente se necessário.
                                                    </p>
                                                    <input
                                                        type="text"
                                                        className={styles.reposicaoInput}
                                                        placeholder="0,00"
                                                        value={reposicaoEdits.valor || ''}
                                                        onChange={e => setReposicaoEdits(prev => ({
                                                            ...prev,
                                                            valor: formatInputToBRL(e.target.value)
                                                        }))}
                                                    />
                                                </div>
                                            </div>

                                            <div className={styles.reposicaoActionRow}>
                                                {((complaint.infoReposicao && complaint.infoReposicao.trim() !== '') || (complaint.valorReposicao && Number(complaint.valorReposicao) > 0)) ? (
                                                    <button
                                                        className={styles.cancelReposicaoBtn}
                                                        onClick={() => {
                                                            setReposicaoEdits({
                                                                valor: formatFloatToBRL(complaint.valorReposicao),
                                                                info: complaint.infoReposicao || ''
                                                            });
                                                            if (complaint.infoReposicao) {
                                                                const entries = complaint.infoReposicao.split(',').map(c => c.trim()).filter(Boolean);
                                                                setSelectedOrders(entries.map(entry => {
                                                                    const match = entry.match(/^#?(\d+)\s*-\s*(.*)$/);
                                                                    if (match) {
                                                                        return { code: match[1], clientName: match[2] } as OrderSearchResult;
                                                                    }
                                                                    const pureCode = entry.replace(/^#/, '');
                                                                    return { code: pureCode } as OrderSearchResult;
                                                                }));
                                                            } else {
                                                                    setSelectedOrders([]);
                                                            }
                                                            setIsEditingReposicao(false);
                                                        }}
                                                        disabled={savingReposicao}
                                                    >
                                                        Cancelar
                                                    </button>
                                                ) : null}
                                                <button
                                                    className={styles.saveReposicaoBtn}
                                                    onClick={() => handleSaveReposicao()}
                                                    disabled={savingReposicao}
                                                >
                                                    {savingReposicao ? 'Salvando...' : 'Salvar Reposição'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={styles.reposicaoSummaryGrid}>
                                            <div className={styles.reposicaoSummaryCol}>
                                                <span className={styles.summaryLabel}>Pedidos Vinculados</span>
                                                <div className={styles.readOnlyBadgeContainer} style={{ marginTop: '4px' }}>
                                                    {complaint.infoReposicao ? complaint.infoReposicao.split(',').map((info, idx) => (
                                                        <span key={idx} className={styles.readOnlyOrderBadge}>{info.trim()}</span>
                                                    )) : <span className={styles.readOnlyValue}>Nenhum pedido selecionado</span>}
                                                </div>
                                            </div>
                                            <div className={styles.reposicaoSummaryCol}>
                                                <span className={styles.summaryLabel}>Valor da Reposição</span>
                                                <div className={styles.reposicaoValShow}>
                                                    R$ {complaint.valorReposicao ? formatFloatToBRL(complaint.valorReposicao) : '0,00'}
                                                </div>
                                            </div>
                                            <div className={styles.reposicaoSummaryCol} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                <button
                                                    className={styles.editReposicaoBtn}
                                                    onClick={handleStartEditReposicao}
                                                    style={{ margin: 0 }}
                                                >
                                                    Editar Reposição
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={styles.tabContentArea}>
                        {/* Secondary Section: Order Information & Segment (Context) */}
                        <div className={styles.infoCard}>
                            <div className={styles.cardHeader}>
                                <InfoIcon fontSize="small" className={styles.cardIcon} />
                                <span className={styles.cardTitle}>Informações da Venda</span>
                            </div>
                            <div className={styles.cardContentGrid}>
                                <div className={styles.miniGroup}>
                                    <label>Pedido Origem</label>
                                    <div className={styles.valueWithLink}>
                                        #{complaint.pedidoCodigo || complaint.vendaId}
                                        <button
                                            className={styles.linkButton}
                                            onClick={() => onViewOrder && onViewOrder(Number(complaint.vendaId))}
                                            title="Ver detalhes do pedido"
                                        >
                                            Ver Pedido
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.miniGroup}>
                                    <label>Emissão</label>
                                    <div>{formatDate(complaint.pedidoEmissao)}</div>
                                </div>
                                <div className={styles.miniGroup}>
                                    <label>Segmento do Cliente</label>
                                    <div>{complaint.segmento || '-'}</div>
                                </div>
                                <div className={styles.miniGroup}>
                                    <label>Tipo de Ressarcimento</label>
                                    <div style={{ fontWeight: 600, color: '#1e293b' }}>
                                        {getRefundLabel(complaint.tipoRessarcimento)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Items Section */}
                        <div className={styles.itemsSection}>
                            <div className={styles.itemsHeader}>
                                <h3 className={styles.sectionTitle}>
                                    <ListAltIcon fontSize="small" className={styles.cardIcon} />
                                    Itens Reclamados
                                    <span className={styles.itemCountBadge}>{complaint.items?.length || 0}</span>
                                </h3>
                                <div className={styles.totalBox}>
                                    <span className={styles.totalLabel}>Total de Pares Reclamados:</span>
                                    <span className={styles.totalValue}>
                                        {complaint.items?.reduce((acc, item) => {
                                            const itemTotal = item.quantidades
                                                ? Object.values(item.quantidades).reduce((qAcc, q) => qAcc + q, 0)
                                                : 0;
                                            return acc + itemTotal;
                                        }, 0) || 0}
                                    </span>
                                </div>
                            </div>

                            {complaint.items && complaint.items.length > 0 ? (
                                <div className={styles.tableContainer}>
                                    <div className={styles.tableHeaderRow}>
                                        <div className={styles.colRef}>Referência</div>
                                        <div className={styles.colProd}>Produto</div>
                                        <div className={styles.colInfo}>Detalhes (C.A / Lote)</div>
                                        <div className={styles.colQtd}>Qtd.</div>
                                    </div>
                                    <div className={styles.tableBody}>
                                        {complaint.items.map((item) => {
                                            const totalQty = item.quantidades
                                                ? Object.values(item.quantidades).reduce((acc, curr) => acc + curr, 0)
                                                : 0;

                                            return (
                                                <div key={item.id} className={styles.tableRow}>
                                                    <div className={styles.colRef}>
                                                        <span className={styles.referenceBadge}>{item.referencia}</span>
                                                    </div>
                                                    <div className={styles.colProd}>
                                                        <div className={styles.prodName}>{item.descricao}</div>
                                                        {item.observacao && (
                                                            <div className={styles.observationBox}>
                                                                <strong>Obs:</strong> {item.observacao}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={styles.colInfo}>
                                                        <div className={styles.infoTag}><span className={styles.infoTagLabel}>C.A:</span> {item.ca || '-'}</div>
                                                        <div className={styles.infoTag}>
                                                            <span className={styles.infoTagLabel}>Lote:</span>
                                                            <div className={styles.loteContainer}>
                                                                {item.lote ? (
                                                                    item.lote.split(',').map((lote, idx) => (
                                                                        <span key={idx} className={styles.loteBadge}>
                                                                            {lote.trim()}
                                                                        </span>
                                                                    ))
                                                                ) : (
                                                                    '-'
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className={styles.infoTag}><span className={styles.infoTagLabel}>Comprado:</span> {item.paresComprados}</div>
                                                    </div>
                                                    <div className={styles.colQtd}>
                                                        <div className={styles.qtyContainer}>
                                                            <div className={styles.totalRow}>
                                                                <span className={styles.totalLabelSmall}>Total</span>
                                                                <span className={styles.totalValueBadge}>{totalQty}</span>
                                                            </div>
                                                            <div className={styles.sizeGrid}>
                                                                {item.quantidades && Object.entries(item.quantidades).map(([size, qty]) => (
                                                                    <div key={size} className={styles.sizeBox}>
                                                                        <span className={styles.sizeLabel}>{size}</span>
                                                                        <span className={styles.sizeValue}>{qty}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.emptyState}>
                                    Nenhum item registrado.
                                </div>
                            )}
                        </div>

                        {/* Images Gallery */}
                        <div className={styles.imageSection}>
                            <div className={styles.imageGroup}>
                                <div className={styles.cardHeader}>
                                    <PhotoLibraryIcon fontSize="small" className={styles.cardIcon} />
                                    <span className={styles.cardTitle}>Evidências Fotográficas ({complaint.fotosUrls?.length || 0})</span>
                                </div>
                                {!complaint.fotosUrls || complaint.fotosUrls.length === 0 ? (
                                    <div className={styles.emptyState}>Nenhuma foto anexada.</div>
                                ) : (
                                    <div className={styles.imageGrid}>
                                        {complaint.fotosUrls.map((url, index) => (
                                            <div key={`foto-${index}`} className={styles.imageCard} onClick={() => setPreviewIndex({ index, type: 'foto' })}>
                                                <img src={url} alt={`Foto ${index + 1}`} className={styles.complaintImage} loading="lazy" title="Clique para ampliar" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={styles.imageGroup}>
                                <div className={styles.cardHeader}>
                                    <PhotoLibraryIcon fontSize="small" className={styles.cardIcon} />
                                    <span className={styles.cardTitle}>Fotos das Etiquetas ({complaint.etiquetasUrls?.length || 0})</span>
                                </div>
                                {!complaint.etiquetasUrls || complaint.etiquetasUrls.length === 0 ? (
                                    <div className={styles.emptyState}>Nenhuma etiqueta anexada.</div>
                                ) : (
                                    <div className={styles.imageGrid}>
                                        {complaint.etiquetasUrls.map((url, index) => (
                                            <div key={`etiqueta-${index}`} className={styles.imageCard} onClick={() => setPreviewIndex({ index, type: 'etiqueta' })}>
                                                <img src={url} alt={`Etiqueta ${index + 1}`} className={styles.complaintImage} loading="lazy" title="Clique para ampliar" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Image Viewer Overlay */}
            {previewIndex !== null && (
                <ImageOverlay
                    images={previewIndex.type === 'foto' ? (complaint?.fotosUrls || []) : (complaint?.etiquetasUrls || [])}
                    initialIndex={previewIndex.index}
                    onClose={() => setPreviewIndex(null)}
                />
            )}

            {/* Toast Overlay */}
            {toast.show && (
                <div className={styles.toastOverlay} onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <div className={`${styles.toastCard} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`} onClick={e => e.stopPropagation()}>
                        <div className={styles.toastIconWrapper}>
                            {toast.type === 'success' ? (
                                <CheckCircleOutlineIcon className={styles.toastIcon} />
                            ) : (
                                <ErrorOutlineIcon className={styles.toastIcon} />
                            )}
                        </div>
                        <p className={styles.toastMessage}>{toast.message}</p>
                        <button className={styles.toastCloseBtn} onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                            Fechar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ComplaintDetailView;
